import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  getOrCreateTrailingEngine,
  getTrailingEngine,
  getAllTrailingEngines,
  normalizeTrailingParams,
  type TrailingStopParams,
} from '@/lib/trailing-stop-engine';
import { buildSessionPrefix, parseSessionKey } from '@/lib/session-key';
import { getDeviceWriterId } from '@/lib/device-id';

// ─── Types ──────────────────────────────────────────────────

interface TrailingStrategyRow {
  id: string;
  name: string;
  active_accounts: string[];
  trailingstopparams: TrailingStopParams | null;
}

interface TrailingSessionRow {
  strategy_id: string;
  session_data: Record<string, unknown>;
}

interface TrailingPersistedSession {
  selectedAccountId: string;
  localParams: TrailingStopParams;
  wasRunning: boolean;
  lastActive: number;
}

// ─── Hook ───────────────────────────────────────────────────

export function useTrailingGlobalRestore(userId: string | null) {
  const restoredRef = useRef(false);
  const retryCountRef = useRef(0);

  useEffect(() => {
    // ─── Logout: stop all trailing engines ─────────────────
    if (!userId) {
      const engines = getAllTrailingEngines();
      for (const engine of engines) {
        engine.stop();
      }
      restoredRef.current = false;
      return;
    }

    if (restoredRef.current) return;
    restoredRef.current = true;

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [2000, 4000, 8000, 16000, 30000];

    // ─── Query per-account sessions for a strategy ─────────

    const querySessionsForStrategy = async (
      stratId: string,
    ): Promise<Map<string, TrailingPersistedSession>> => {
      const accountSessions = new Map<string, TrailingPersistedSession>();

      try {
        // Query composite keys: {strategyId}__account_%
        const prefix = buildSessionPrefix(stratId);
        const { data: compositeRows, error: compositeErr } = await supabase
          .from('bot_session_state')
          .select('strategy_id, session_data')
          .eq('user_id', userId)
          .like('strategy_id', prefix);

        if (!compositeErr && compositeRows) {
          for (const row of (compositeRows as unknown as TrailingSessionRow[])) {
            const parsed = parseSessionKey(row.strategy_id);
            if (parsed && row.session_data?.type === 'trailing_stop') {
              accountSessions.set(parsed.accountId, {
                selectedAccountId: (row.session_data.selectedAccountId as string) || parsed.accountId,
                localParams: normalizeTrailingParams(row.session_data.localParams as Partial<TrailingStopParams> | null | undefined),
                wasRunning: !!row.session_data.wasRunning,
                lastActive: (row.session_data.lastActive as number) || 0,
              });
            }
          }
        }

        // Fallback: try legacy key (just strategyId)
        if (accountSessions.size === 0) {
          const { data: legacyRow, error: legacyErr } = await supabase
            .from('bot_session_state')
            .select('session_data')
            .eq('user_id', userId)
            .eq('strategy_id', stratId)
            .maybeSingle();

          if (!legacyErr && legacyRow?.session_data && legacyRow.session_data.type === 'trailing_stop') {
            const raw = legacyRow.session_data;
            const legacyAccount = (raw.selectedAccountId as string) || '';
            if (legacyAccount) {
              accountSessions.set(legacyAccount, {
                selectedAccountId: legacyAccount,
                localParams: normalizeTrailingParams(raw.localParams as Partial<TrailingStopParams> | null | undefined),
                wasRunning: !!raw.wasRunning,
                lastActive: (raw.lastActive as number) || 0,
              });
              console.log(`[TrailingGlobalRestore] 📦 Using legacy session for strategy account=${legacyAccount}`);
            }
          }
        }
      } catch (err) {
        console.warn('[TrailingGlobalRestore] Session query error:', err);
      }

      return accountSessions;
    };

    const tryRestore = async (attempt: number) => {
      try {
        // Step 1: Fetch all trailing_stop strategies for this user
        const { data: strategies, error } = await supabase
          .from('strategies')
          .select('id, name, active_accounts, trailingstopparams')
          .eq('type', 'trailing_stop')
          .eq('user_id', userId);

        if (error) {
          console.warn('[TrailingGlobalRestore] DB fetch error:', error.message);
          scheduleRetry(attempt);
          return;
        }

        if (!strategies || (strategies as unknown[]).length === 0) {
          console.log('[TrailingGlobalRestore] No trailing stop strategies found');
          return;
        }

        const rows = strategies as unknown as TrailingStrategyRow[];

        // Step 2: Check cTrader gateway status
        let gatewayAccounts: Array<{ account_id: string }> = [];
        let gatewayReady = false;
        try {
          const { data: gwData } = await supabase.functions.invoke<{
            connected: boolean;
            accounts: Array<{ account_id: string }>;
          }>('ctrader-gateway', {
            method: 'POST',
            body: { action: 'status' },
          });
          if (gwData?.connected && gwData?.accounts && gwData.accounts.length > 0) {
            gatewayAccounts = gwData.accounts;
            gatewayReady = true;
            console.log(`[TrailingGlobalRestore] Gateway online with ${gatewayAccounts.length} account(s)`);
          } else {
            console.log('[TrailingGlobalRestore] Gateway not ready yet');
          }
        } catch {
          console.log('[TrailingGlobalRestore] Gateway unreachable, will retry');
        }

        if (!gatewayReady) {
          scheduleRetry(attempt);
          return;
        }

        // Step 3: Query all per-account sessions for all strategies
        let startedCount = 0;
        for (const strat of rows) {
          const activeAccounts = Array.isArray(strat.active_accounts) ? strat.active_accounts : [];
          if (activeAccounts.length === 0) continue;

          const accountSessions = await querySessionsForStrategy(strat.id);
          console.log(`[TrailingGlobalRestore] Found ${accountSessions.size} session(s) for strategy "${strat.name}"`);

          for (const accId of activeAccounts) {
            const accountExists = gatewayAccounts.some(
              (a) => String(a.account_id) === String(accId),
            );
            if (!accountExists) continue;

            const existingEngine = getTrailingEngine(strat.id, accId);
            if (existingEngine && existingEngine.isRunning && existingEngine.selectedAccountId === accId) {
              console.log(`[TrailingGlobalRestore] Engine already running for ${strat.name} / ${accId}`);
              // Update params in case they changed
              const persisted = accountSessions.get(accId);
              if (persisted) existingEngine.setParams(persisted.localParams);
              startedCount++;
              continue;
            }

            // Get per-account params: persisted > strategy default
            const persisted = accountSessions.get(accId);
            const params = normalizeTrailingParams(persisted?.localParams || strat.trailingstopparams);

            try {
              const engine = getOrCreateTrailingEngine(strat.id, accId, params);
              await engine.init(accId);
              engine.start();
              startedCount++;
              console.log(`[TrailingGlobalRestore] ✓ Started trailing for ${strat.name} on account ${accId}`);
            } catch (initErr) {
              console.warn(
                `[TrailingGlobalRestore] Init failed for ${strat.name}/${accId}:`,
                (initErr as Error).message,
              );
            }
          }
        }

        console.log(`[TrailingGlobalRestore] Done: ${startedCount} trailing engine(s) started`);

        if (startedCount === 0 && rows.some((s) => (Array.isArray(s.active_accounts) ? s.active_accounts : []).length > 0)) {
          scheduleRetry(attempt);
        }
      } catch (err) {
        console.error('[TrailingGlobalRestore] Unexpected error:', err);
        scheduleRetry(attempt);
      }
    };

    const scheduleRetry = (currentAttempt: number) => {
      const nextAttempt = currentAttempt + 1;
      if (nextAttempt < MAX_RETRIES) {
        retryCountRef.current = nextAttempt;
        console.log(
          `[TrailingGlobalRestore] Scheduling retry ${nextAttempt}/${MAX_RETRIES - 1} in ${RETRY_DELAYS[currentAttempt] / 1000}s...`,
        );
        setTimeout(() => tryRestore(nextAttempt), RETRY_DELAYS[currentAttempt]);
      } else {
        console.warn('[TrailingGlobalRestore] Max retries reached, giving up');
      }
    };

    const timer = setTimeout(() => tryRestore(0), 1500);
    return () => clearTimeout(timer);
  }, [userId]);

  // ─── Global live-sync of trailing settings ──────────────────
  // Applies a trailing-settings change made ELSEWHERE — e.g. the Telegram bot's
  // [Chỉnh TP SL] / [Chống Âm] buttons, which write to `bot_session_state` — to
  // any RUNNING engine immediately, even when the Trailing Monitor is not open.
  // Our own writes are skipped via writerId (the monitor already handles those).
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const myWriterId = getDeviceWriterId();
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const handle = (payload: { new?: { strategy_id?: string; session_data?: Record<string, unknown> } }) => {
      if (cancelled) return;
      const row = payload.new;
      if (!row?.strategy_id) return;
      const parsed = parseSessionKey(row.strategy_id);
      if (!parsed) return;
      const sd = row.session_data;
      if (!sd || sd.type !== 'trailing_stop') return;
      if (sd.writerId === myWriterId) return; // our own write — already applied
      const engine = getTrailingEngine(parsed.strategyId, parsed.accountId);
      if (!engine) return;
      engine.setParams(normalizeTrailingParams(sd.localParams as Partial<TrailingStopParams> | null | undefined));
      console.log('[TrailingGlobalRestore] 🔄 Applied external trailing settings change (live)');
    };

    channel = supabase
      .channel(`trailing-global-sync-${userId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'bot_session_state', filter: `user_id=eq.${userId}` }, handle)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'bot_session_state', filter: `user_id=eq.${userId}` }, handle)
      .subscribe();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [userId]);
}